import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertOctagon, Send, CheckCircle2, Clock, MailWarning } from "lucide-react";
import { toast } from "sonner";

function timeSince(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function OverduePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/rents/overdue");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remind = async (rent) => {
    setSending(s => ({ ...s, [rent.id]: true }));
    try {
      const { data } = await api.post(`/rents/${rent.id}/remind`);
      if (data.email_status === "sent") {
        toast.success(`Reminder sent to ${rent.tenant_email} · in-app + email`);
      } else if (data.email_status === "failed") {
        toast.warning(`In-app reminder created. Email failed: ${data.email_error?.slice(0, 120) || "unknown"}`);
      } else {
        toast.success("In-app reminder created (email not configured)");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSending(s => ({ ...s, [rent.id]: false }));
  };

  const totalAmount = items.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <AppLayout title="Overdue Invoices" subtitle="Rent unpaid past the 5th of its month — send a friendly nudge">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="p-6 border-slate-200" data-testid="overdue-stat-count">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Overdue invoices</div>
              <div className="font-display text-4xl font-semibold mt-3 font-mono-num">{items.length}</div>
              <div className="text-xs text-slate-500 mt-1">need attention</div>
            </div>
            <div className="h-10 w-10 rounded-md bg-rose-50 flex items-center justify-center"><AlertOctagon className="h-5 w-5 text-rose-600" /></div>
          </div>
        </Card>
        <Card className="p-6 border-slate-200" data-testid="overdue-stat-amount">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Outstanding total</div>
              <div className="font-display text-4xl font-semibold mt-3 font-mono-num">${totalAmount.toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-1">to collect</div>
            </div>
            <div className="h-10 w-10 rounded-md bg-amber-50 flex items-center justify-center"><Clock className="h-5 w-5 text-amber-700" /></div>
          </div>
        </Card>
        <Card className="p-6 border-slate-200" data-testid="overdue-stat-reminders">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Reminders sent</div>
              <div className="font-display text-4xl font-semibold mt-3 font-mono-num">{items.reduce((s, r) => s + (r.reminder_count || 0), 0)}</div>
              <div className="text-xs text-slate-500 mt-1">across all overdue</div>
            </div>
            <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center"><Send className="h-5 w-5 text-slate-700" /></div>
          </div>
        </Card>
      </div>

      <Card className="border-slate-200">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-display font-semibold">Overdue list</h3>
          <p className="text-xs text-slate-500 mt-0.5">Invoices unpaid past the 5th. Send a reminder to deliver an in-app notification + email to the tenant.</p>
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center" data-testid="overdue-empty">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
            <div className="font-display font-semibold mb-1">All caught up</div>
            <div className="text-sm text-slate-500">No overdue invoices. Beautiful.</div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Period</TableHead><TableHead>Tenant</TableHead><TableHead>Property</TableHead><TableHead>Amount</TableHead><TableHead>Overdue</TableHead><TableHead>Reminders</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(r => (
                <TableRow key={r.id} data-testid={`overdue-${r.id}`}>
                  <TableCell className="font-mono-num">{r.period_month}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{r.tenant_name}</div>
                    <div className="text-xs text-slate-500">{r.tenant_email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{r.property_name}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[200px]">{r.property_address}</div>
                  </TableCell>
                  <TableCell className="font-mono-num">${r.amount}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.days_overdue > 14 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.days_overdue} day{r.days_overdue !== 1 && "s"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {r.reminder_count > 0 ? (
                      <div>
                        <div className="font-mono-num">{r.reminder_count} sent</div>
                        <div className="text-slate-400">last: {timeSince(r.last_reminder)}</div>
                      </div>
                    ) : (
                      <span className="text-slate-400">never</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`send-reminder-${r.id}`}
                      onClick={() => remind(r)}
                      disabled={sending[r.id]}
                      size="sm"
                      className="bg-slate-900 hover:bg-slate-800 gap-1.5"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {sending[r.id] ? "Sending…" : r.reminder_count > 0 ? "Resend" : "Send Reminder"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="mt-6 p-4 border-slate-200 bg-amber-50 border-amber-200" data-testid="resend-sandbox-notice">
        <div className="flex items-start gap-3">
          <MailWarning className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <strong>Email delivery note:</strong> Resend is currently in sandbox mode — emails can only be delivered to the address you signed up with at resend.com. To send to actual tenants, verify a domain at <span className="font-mono">resend.com/domains</span> and update <span className="font-mono">SENDER_EMAIL</span> in your backend env. In-app notifications work for everyone immediately.
          </div>
        </div>
      </Card>
    </AppLayout>
  );
}
